import * as formik from "formik";
import * as yup from "yup";

import { Col, Form, Modal, Row } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";

import AppButton from "../../../commons/buttons/AppButton";
import AppCurrencyInputV2 from "../../../commons/inputs/AppCurrencyInputV2";
import AppInputSelectV2 from "../../../commons/inputs/AppInputSelectV2";
import AppInputV2 from "../../../commons/inputs/AppInputV2";
import AppLoading from "../../../commons/AppLoading";
import AppSelectV2 from "../../../commons/inputs/AppSelectV2";
import ConfirmModal from "../../../commons/modal/ConfirmModal";
import HallAppToast from "../../../commons/HallAppToast";
import ProductSaleAPI from "../ProductInventoryMovementAPI copy";

const api = new ProductSaleAPI();

export default function ModalSellProduct({
    show,
    closeShow,
    product,
    employees = [],
    onSuccess,
}) {
    const { Formik } = formik;

    const [isLoading, setIsLoading] = useState(false);
    const [showNoStockModal, setShowNoStockModal] = useState(false);

    const currentStock = Number(product?.inventory?.currentQuantity || 0);

    useEffect(() => {
        if (show && currentStock <= 0) {
            setShowNoStockModal(true);
        }
    }, [show, currentStock]);

    const SALE_TYPE = useMemo(
        () => [
            {
                value: "CLIENT",
                title: "Venda para cliente",
                subtitle: "Venda para cliente final",
            },
            {
                value: "EMPLOYEE",
                title: "Venda para profissional",
                subtitle: "Venda interna para profissional",
            },
        ],
        []
    );

    const schema = yup.object().shape({
        quantity: yup
            .number()
            .required("Informe a quantidade")
            .moreThan(0, "Deve ser maior que zero"),
        saleType: yup.string().required("Selecione o tipo de venda"),
        employee: yup.object().nullable().required("Selecione o profissional"),
    });

    const initialValues = useMemo(
        () => ({
            quantity: "",
            saleType: "CLIENT",
            employee: null,
            unitPrice: product?.pricing?.customerSalePrice || 0,
        }),
        [product]
    );

    const resolvePrice = (saleType) => {
        if (saleType === "EMPLOYEE") {
            return Number(product?.pricing?.employeeSalePrice || 0);
        }
        return Number(product?.pricing?.customerSalePrice || 0);
    };

    const calculateTotal = (quantity, unitPrice) => {
        const q = Number(quantity || 0);
        const p = Number(unitPrice || 0);
        return q * p;
    };

    const formatCurrency = (value) => {
        return Number(value || 0).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
        });
    };

    const save = async (values) => {
        if (!product?.id) return;

        try {
            setIsLoading(true);

            await api.create({
                productId: product.id,
                employeeId: values.employee?.value,
                saleType: values.saleType,
                quantity: Number(values.quantity),
                observation: values.observation || null,
            });

            HallAppToast.success("Venda realizada com sucesso");
            onSuccess && onSuccess();
            closeShow();
        } catch (e) {
            HallAppToast.genericError();
        } finally {
            setIsLoading(false);
        }
    };

    if (!product) return null;

    return (
        <>
            <Modal show={show} onHide={closeShow} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Venda - {product.name}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Formik
                        validationSchema={schema}
                        initialValues={initialValues}
                        onSubmit={save}
                        enableReinitialize
                    >
                        {({
                            handleSubmit,
                            handleChange,
                            values,
                            setFieldValue,
                            errors,
                            touched,
                        }) => {
                            const total = calculateTotal(
                                values.quantity,
                                values.unitPrice
                            );

                            return (
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col xs={12}>
                                            <AppInputSelectV2
                                                name="saleType"
                                                value={values.saleType}
                                                onChange={(e) => {
                                                    handleChange(e);

                                                    const nextType = e?.target?.value;
                                                    const price = resolvePrice(nextType);

                                                    setFieldValue("unitPrice", price);
                                                }}
                                                options={SALE_TYPE}
                                                isSearchable={false}
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xs={12}>
                                            <AppSelectV2
                                                label={
                                                    values.saleType === "EMPLOYEE"
                                                        ? "Profissional (compra interna)"
                                                        : "Profissional (quem vendeu)"
                                                }
                                                options={employees}
                                                value={values.employee}
                                                onChange={(opt) =>
                                                    setFieldValue("employee", opt)
                                                }
                                                errors={errors}
                                                touched={touched}
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xs={12}>
                                            <AppInputV2
                                                name="quantity"
                                                label="Quantidade"
                                                type="number"
                                                value={values.quantity}
                                                onChange={handleChange}
                                                errors={errors}
                                                touched={touched}
                                                isRequired
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xs={12}>
                                            <AppCurrencyInputV2
                                                label="Preço unitário"
                                                value={values.unitPrice}
                                                disabled
                                            />
                                        </Col>
                                    </Row>

                                    {/* 🔥 TOTAL DESTACADO */}
                                    <div
                                        style={{
                                            marginTop: 16,
                                            padding: 12,
                                            borderRadius: 10,
                                            background: "#f0fdf4",
                                            border: "1px solid #bbf7d0",
                                        }}
                                    >
                                        <div style={{ fontSize: 12, color: "#166534" }}>
                                            Valor total
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 20,
                                                fontWeight: 700,
                                                color: "#15803d",
                                            }}
                                        >
                                            {formatCurrency(total)}
                                        </div>
                                    </div>

                                    <div style={{ marginTop: 16 }}>
                                        {isLoading ? (
                                            <AppLoading />
                                        ) : (
                                            <AppButton
                                                type="submit"
                                                name="Confirmar venda"
                                                width="100%"
                                            />
                                        )}
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </Modal.Body>
            </Modal>

            <ConfirmModal
                show={showNoStockModal}
                title="Sem estoque"
                textBtnConfirm="OK"
                confirm={() => setShowNoStockModal(false)}
                closeModal={() => {
                    setShowNoStockModal(false);
                    closeShow();
                }}
            >
                Este produto não possui estoque disponível para venda.
            </ConfirmModal>
        </>
    );
}